






















































































import { Component, Mixins, Provide } from "vue-property-decorator";
import { Action, Getter, Mutation, namespace } from "vuex-class";
import { get } from "lodash";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin.ts";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { KeyboardShortcut, LegendKey, UserPermissions } from "@/store/types";
import SiteMonitorHeader from "@/components/SiteMonitorHeader.vue";
import EnhancedMap from "@/components/EnhancedMap.vue";
import EventTaskContainer from "@/components/tasks/EventTaskContainer.vue";
import EventClose from "@/components/EventClose.vue";
import EventRaise from "@/components/EventRaise.vue";
import SureViewIcon from "@/components/SureViewIcon.vue";

import AreaNotes from "@/components/AreaNotes.vue";
import EventSharing from "@/components/EventSharing.vue";
import EventLeave from "@/components/EventLeave.vue";
import IncidentReport from "@/components/IncidentReport.vue";
import ErrorWarning from "@/components/ErrorWarning.vue";
import EventEnded from "@/components/EventEnded.vue";
import ForceRestore from "@/components/ForceRestore.vue";
import InactivityWarning from "@/components/site-monitor/InactivityWarning.vue";
import AlarmPointDialog from "@/components/alarm-points/AlarmPointDialog.vue";
import AreaNotesModal from "@/components/area-notes/AreaNotesModal.vue";
import AcknowledgeAreaNotesModal from "@/components/site-monitor/AcknowledgeAreaNotesModal.vue";
import PutEventOnTestModal from "@/components/site-monitor/PutEventOnTestModal.vue";
import HighPriorityCheck from "@/components/HighPriorityCheck.vue";
import AlarmNotification from "@/components/site-monitor/AlarmNotification.vue";
import ViewsEventBus from "@/services/views/viewsEventBus";
import { AlarmQueueFilterID, FilteredEvent } from "@/store/eventqueue/types";
import { EventRegion, EventRecord, EventDetails } from "@/store/site-monitor/types";
import { MessageBroadcastChannel } from "@/utils/messageBroadcastChannel";
import { MatrixEvents } from "@/types/MatrixEvents";
import EventTypes from "@/types/EventTypes";

const SiteMonitorStore = namespace("siteMonitor");
const EventQueueStore = namespace("eventqueue");
const Regions = namespace("regions");

@Component({
	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"site-monitor-header": SiteMonitorHeader,
		"high-priority-check": HighPriorityCheck,
		"event-records": () => import("@/components/site-monitor/event-records/EventRecords.vue"),
		"event-records-ccure": () => import("@/components/site-monitor/event-records/EventRecordsCCure.vue"),
		"event-records-ccure-tasks": () => import("@/components/site-monitor/event-records/EventRecordsCCureTasks.vue"),
		"enhanced-map": EnhancedMap,
		"task-container": EventTaskContainer,
		"event-close": EventClose,
		"event-raise": EventRaise,
		"alarm-notification": AlarmNotification,
		"area-notes": AreaNotes,
		"area-notes-modal": AreaNotesModal,
		"acknowledge-area-notes-modal": AcknowledgeAreaNotesModal,
		"event-sharing": EventSharing,
		"event-leave": EventLeave,
		"incident-report": IncidentReport,
		"error-warning": ErrorWarning,
		"event-ended": EventEnded,
		"force-restore": ForceRestore,
		"inactivity-warning": InactivityWarning,
		"alarm-point-dialog": AlarmPointDialog,
		"put-event-on-test-modal": PutEventOnTestModal,
		"sureview-icon": SureViewIcon
	}
})
export default class SiteMonitor extends Mixins(RegionalHandlerMixin) {
	// -- Vuex -- //
	@Getter("getPermissions") private permissions: UserPermissions;

	@Regions.Getter private getRegionUrl: (regionId: number) => string;

	@Action private openMediaMatrix: () => Promise<void>;

	@Mutation private setShortcuts: (shortCuts: KeyboardShortcut[]) => void;
	@Mutation private setLegendKeys: (legends: LegendKey[]) => void;
	@Mutation private setRoutingGroupsLegend: (show: boolean) => void;

	@SiteMonitorStore.State private eventRegion: EventRegion;
	@SiteMonitorStore.Action private fetchEventDetails: (eventId: number) => Promise<void>;

	// Getter and action for the flag for toggling the map.
	@SiteMonitorStore.Action private setHideMapFlag: (hideMapFlag: boolean) => void;
	@SiteMonitorStore.Getter("getHideMapFlag") private hideMapFlag: boolean;

	// Getters which are used to share data with the MM.
	@SiteMonitorStore.Getter("getEventDetails") private eventDetails: EventDetails;
	@SiteMonitorStore.Getter("getSelectedEventRecord") private selectedEventRecord: EventRecord;
	@SiteMonitorStore.Getter("getIsController") private isController: boolean;
	@SiteMonitorStore.Getter("getMapCircleCenter") private mapCircleCenter: any;

	// Mutations which are currently shared with the MM.
	@SiteMonitorStore.Mutation private setEventDetails: (eventDetails: EventDetails) => void;
	@SiteMonitorStore.Mutation private setSelectedEventRecord: (eventRecord: EventRecord) => void;
	@SiteMonitorStore.Mutation private setIsController: (isController: boolean) => void;
	@SiteMonitorStore.Mutation private setMapCircleCenter: (center: any) => void;

	@EventQueueStore.State(state => state.FilteredEvents) filteredEvents: FilteredEvent[];
	@EventQueueStore.Action fetchEventQueue: (filters?: AlarmQueueFilterID[]) => Promise<void>;
	@EventQueueStore.Mutation("setJoiningEventID") private setJoiningEventId: (eventId: number) => void;
	@EventQueueStore.Getter("getJoiningEventID") private joiningEventId: number;

	@Provide("viewsEventBus")
	private eventBus: ViewsEventBus = new ViewsEventBus();

	@Provide("matrixChannel")
	private mediaMatrixChannel: MessageBroadcastChannel = new MessageBroadcastChannel("matrix");

	// -- Properties -- //
	private fetchEventQueueInterval: NodeJS.Timeout = null;
	private eventQueueIntervalTime: number = 2000;

	/**
	 * Controls visibility of child components/loading event screen.
	 */
	private siteMonitorReady: boolean = false;

	/**
	 * Returns the event ID passed in through our route as a number.
	 */
	public get eventId(): number {
		return parseInt(this.$route.params.eventId) || 0;
	}

	/**
	 * Returns the event record component type to use, depending on whether or not
	 * CCure and Tasks is enabled.
	 */
	public get eventRecordType() {
		let useCCureEventRecords = get(this.featuresList, ["Integration", "CCure", "UI"]);

		if (!useCCureEventRecords) {
			return "event-records";
		} else {
			let useTasksRecords = get(this.featuresList, ["Integration", "Tasks"]);
			return useTasksRecords ? "event-records-ccure-tasks" : "event-records-ccure";
		}
	}

	/**
	 * Returns true if the High Priority Banner is enabled.
	 */
	public get isHighPriorityBannerEnabled(): boolean {
		return (
			get(this.featuresList, ["Alarms", "HighPriorityBanner"]) && this.permissions.canInteractHighPriorityBanner
		);
	}

	/**
	 * Returns true if the Map is enabled.
	 */
	public get mapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "Map"]);
	}

	/**
	 * Returns true if the new Area Notes is enabled.
	 */
	private get newAreaNotesEnabled(): boolean {
		return get(this.featuresList, ["AreaNotes"]) && get(this.featuresList, ["AreaNotes", "Icons"]);
	}

	/**
	 * Returns true if the old Area Notes is enabled.
	 */
	private get areaNotesEnabled(): boolean {
		return get(this.featuresList, ["AreaNotes"]) && !get(this.featuresList, ["AreaNotes", "Icons"]);
	}

	/**
	 * Returns true if the user can toggle to hide/show the Map.
	 */
	private get isToggleMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "Maps", "ToggleSiteMonitor"]);
	}

	/**
	 * Returns true if the user can perform a Camera Search in the Map.
	 */
	private get isCameraSearchEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "CameraSearch"]);
	}

	/**
	 * Returns true if Alarm Notifications is enabled.
	 */
	private get isAlarmNotificationEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "AlarmQueueStatus"]);
	}

	/**
	 * Checks any feature that require event queue polling.
	 */
	private get shouldPoll(): boolean {
		return this.isAlarmNotificationEnabled || this.isHighPriorityBannerEnabled;
	}

	private get isTestEvent(): boolean {
		return (this.eventDetails?.eventTypeID == EventTypes.OnTest
			||this.eventDetails?.eventTypeID == EventTypes.AuditTest) ?? false;
	}

	/**
	 * Sets up Site Monitor shortcuts and opens the media matrix.
	 */
	public async created() {
		this.setRoutingGroupsLegend(false);
		this.setLegendKeys([{ icon: "", details: "None to show" }]);
		this.setShortcuts([
			{ keys: "Ctrl+E", action: "End event" },
			{ keys: "Ctrl+L", action: "Leave event" },
			{
				keys: "Ctrl+N",
				action: "Incident Report",
				featureFlag: "Alarms.SiteMonitor.SitRep"
			},
			{
				keys: "Ctrl+D",
				action: "Dispatch",
				featureFlag: "Alarms.Dispatch"
			},
			{
				keys: "Ctrl+R",
				action: "Raise",
				featureFlag: "Alarms.SiteMonitor.Raise"
			},
			{
				keys: "Ctrl+S",
				action: "Share Event",
				featureFlag: "Alarms.SiteMonitor.EventSharing"
			},
			{ keys: "Ctrl+I", action: "Area information" },
			{
				keys: "Ctrl+M",
				action: "Open Media Matrix",
				featureFlag: "Alarms.MediaMatrix"
			},
			{
				keys: "Ctrl+G",
				action: "Flag event for review",
				featureFlag: "Alarms.SiteMonitor.EventFlagging"
			}
		]);

		// Setup our MM ready event handler
		this.mediaMatrixChannel.addListener(MatrixEvents.Ready, this.onMediaMatrixIsReady);

		// Open the MM, and create a channel to communicate with it
		await this.openMediaMatrix();

		// If the user leaves or closes the window, ensure we reset the media matrix.
		window.onbeforeunload = () => {
			this.mediaMatrixChannel.postMessage(MatrixEvents.Reset);
		};
	}

	/**
	 * Called when the component is destroyed, clears out the event details, event queue polling
	 * and resets the API region.
	 */
	public async destroyed() {
		if (this.fetchEventQueueInterval) {
			clearInterval(this.fetchEventQueueInterval);
			this.fetchEventQueueInterval = null;
		}
		this.setEventDetails(null);
		await this.resetRegion();
		await this.mediaMatrixChannel.close();
	}

	/**
	 * Starts the polling of the event queue if features are enabled that require event queue polling
	 */
	private setupPolling(): void {
		if (!this.fetchEventQueueInterval) {
			this.fetchEventQueueInterval = setTimeout(this.update, this.eventQueueIntervalTime);
		}
	}

	/**
	 * Fetches the event queue every 2s
	 * This is done using setTimeout rather than setInterval to prevent call stacking
	 * If we used setInterval instead, calls would stack up if the data service took a long time to respond
	 */
	private async update(): Promise<void> {
		const before = performance.now();

		try {
			await this.fetchEventQueue();
		} catch (ex) {
			console.error(ex);
		}

		const after = performance.now();
		const timeTaken = after - before;

		// Run every 2s, taking into account how long the event queue call took
		// If the event queue call took longer than 1990ms wait 10ms before calling again
		this.fetchEventQueueInterval = setTimeout(this.update, Math.max(10, this.eventQueueIntervalTime - timeTaken));
	}

	/**
	 * Handles when the user changes the route, and this instance of site monitor
	 * is re-used (eg, when the user manually changes the event ID in the URL).
	 *
	 * Ensures the user is handling the event, and is in the correct region.
	 */
	private async beforeRouteUpdate(to, from, next) {
		try {
			const eventId = parseInt(to.params.eventId);
			const regionId = parseInt(to.query.regionId || 0);

			this.siteMonitorReady = false;
			await this.onEventChanged(eventId, regionId);
		} catch (err) {
			this.$notify({
				type: "error",
				title: "Site Monitor - Handle Event",
				text:
					"An error occurred while loading the event - please try again later, or contact support if the problem persists."
			});
		} finally {
			this.siteMonitorReady = true;
			next();
		}
	}

	/**
	 * Handles when the user navigates away from site monitor. Clears the
	 * event details and resets our event/API region.
	 */
	private async beforeRouteLeave(to, from, next) {
		try {
			this.siteMonitorReady = false;

			// Re-set the media matrix
			await this.mediaMatrixChannel.postMessage(MatrixEvents.Reset);

			// Clear the event details and reset our region
			this.setEventDetails(null);
			await this.resetRegion();
		} catch (err) {
			// If any errors happen here, we can ignore them as we're leaving the page anyway
		} finally {
			next();
		}
	}

	/**
	 * Handles when the user first navigates to the site monitor. Sets up the user
	 * to handle the event and switches regions if necessary.
	 */
	private async beforeRouteEnter(to, from, next) {
		const eventId = parseInt(to.params.eventId);
		const regionId = parseInt(to.query?.regionId || 0);

		// beforeRouteEnter is called before the component is initialized, so vue-router
		// exposes a callback you can pass to next which allows you to access `vm`.
		next(async (vm: SiteMonitor) => {
			try {
				vm.siteMonitorReady = false;
				await vm.onEventChanged(eventId, regionId);
			} catch (err) {
				vm.$notify({
					type: "error",
					title: "Site Monitor - Handle Event",
					text:
						"An error occurred while loading the event - please try again later, or contact support if the problem persists."
				});
			} finally {
				vm.siteMonitorReady = true;
			}
		});
	}

	/**
	 * Called when the event ID being handled changes. Ensures that the user is able to
	 * handle the event (by calling handleEvent), switches the site monitor over to
	 * the correct event region, and sets up the media matrix via the channel.
	 */
	private async onEventChanged(eventId: number, regionId: number) {
		await this.mediaMatrixChannel.postMessage(MatrixEvents.Reset);

		const regionUrl = this.getRegionUrl(regionId);

		if (this.joiningEventId !== eventId) {
			// If a user navigates directly to this event, ensure the event is being handled.
			await this.handleEventQueueItem(
				{
					eventID: eventId,
					regionID: regionId,
					regionURL: regionUrl
				},
				false
			);
		} else {
			// Otherwise we have already handled this event, clear out the joining variable
			this.setJoiningEventId(null);
		}

		// Check to see if we need to switch regions
		if (this.eventRegion?.regionId != regionId) {
			await this.updateEventRegion({
				regionId: regionId,
				regionUrl: regionUrl
			});
		}

		// Fetch the event details
		await this.fetchEventDetails(eventId);

		// Setup event queue polling
		if (this.shouldPoll) {
			this.setupPolling();
		}
	}

	/**
	 * Handler for when the media matrix notifies us it's ready. If we're still loading the event,
	 * we hold off responding until we're finished.
	 */
	private async onMediaMatrixIsReady() {
		if (this.siteMonitorReady) {
			await this.setupMediaMatrix();
		} else {
			var unwatch = this.$watch(
				() => this.siteMonitorReady,
				ready => {
					if (ready) {
						this.onMediaMatrixIsReady();
						unwatch();
					}
				}
			);
		}
	}

	/**
	 * Sets up the media matrix using the media matrix communication channel. Updates the event
	 * region, waits for confirmation, and then does the same with the event details, before
	 * finally applying legacy (old-style) mutations.
	 */
	private async setupMediaMatrix() {
		// Send the event region to the matrix
		await this.mediaMatrixChannel.postMessage(MatrixEvents.SetEventRegion, this.eventRegion);
		await this.mediaMatrixChannel.waitForMessage(MatrixEvents.SetEventRegionComplete);

		// Send the event to the matrix
		await this.mediaMatrixChannel.postMessage(MatrixEvents.SetEventDetails, this.eventDetails);
		await this.mediaMatrixChannel.waitForMessage(MatrixEvents.SetEventDetailsComplete);

		this.updateMatrixLegacy();
	}

	/**
	 * Legacy method of re-freshing other event information in the matrix.
	 * TODO: Update these to go through the media matrix channel.
	 */
	public updateMatrixLegacy() {
		this.setSelectedEventRecord(this.selectedEventRecord);
		this.setIsController(this.isController);
		this.setMapCircleCenter(this.mapCircleCenter);
	}
}
